import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    AnalysisEvent,
    AnalysisRow,
    AnalysisStep,
    MergeReport,
    useDocumentAnalysisAPI,
} from '../DocumentAnalysisAPI';

/**
 * The document import of the dashboard, on the analysis mechanism.
 *
 * The chimney used to be a placeholder: files dropped on it were listed by name and went nowhere.
 * They now go through the same pipeline the Dokumentenanalyse panel runs — converted, classified,
 * read out — and the list is no longer a local array but the record of those runs. That is the
 * whole change in one sentence: what is on screen is what the server has.
 *
 * Two consequences worth naming, because they shape everything below:
 *
 * - **There is no separate "uploading" list.** A run's record exists from its first second, so a
 *   dropped file appears as a row the moment the server has it. Live progress decorates the row it
 *   belongs to rather than living in a list of its own that would have to be reconciled later.
 * - **A few at a time.** Each run holds an event stream open, and a browser allows only a handful
 *   of connections per host. Ten files dropped at once would starve every other request on the
 *   page — including the ones the streams themselves need.
 */

/**
 * The subtype as a reader would say it.
 *
 * A subtype whose name begins with `x` is a parameter axis — a naming convention of the catalogue,
 * meaning "this is a value to read off, not a type to choose". `xAnlagenart: RLT-Anlage` therefore
 * says the same thing twice, once in a private notation: what was read off is simply "RLT-Anlage".
 */
export const readableSubtype = (payload: any): string | null => {
    const name = payload?.subtype ? String(payload.subtype) : '';
    const value = payload?.parameterValue ? String(payload.parameterValue) : '';

    if (/^x/.test(name) && value) return value;

    return [name, value].filter(Boolean).join(': ') || null;
};

/**
 * Wie viele Dateien gleichzeitig hochgeladen werden.
 *
 * Nur das Hochladen — wie viele *Läufe* gleichzeitig arbeiten, entscheidet seit der Warteschlange
 * der Server. Vorher hing beides zusammen: Der vierte Wurf wartete auf das Ende des ersten Laufs,
 * und die Drosselung lag damit im Browser. Zwei geöffnete Fenster waren zwei Drosselungen, und der
 * Server nahm entgegen, was eintraf.
 */
const MAX_UPLOADS = 3;

/**
 * Wie viele Läufe am Ereignisstrom verfolgt werden.
 *
 * Das ist die Zahl, die wirklich am Browser hängt: Jeder Strom ist eine offene Verbindung, und ein
 * Browser hält je Gegenstelle nur eine Handvoll. Was darüber hinausgeht, wird nicht verfolgt,
 * sondern nachgelesen — der Satz führt seinen Zustand mit, dafür ist er da.
 */
const MAX_WATCHED = 3;

/** Wie oft nachgelesen wird, solange irgendein Lauf noch arbeitet. */
const POLL_MS = 5000;

export interface ImportProgress {
    /** The step being worked on; absent once the run is over. */
    step?: Exclude<AnalysisStep, 'job'>;
    message?: string;
    failed?: boolean;
    finished?: boolean;
    /**
     * What the document was taken to be, as soon as step 2 says so.
     *
     * Read off the stream rather than waited for: the type is the answer the reader is actually
     * after, and it is settled a good while before the extraction that follows it. Holding it back
     * until the run ends would mean staring at a spinner next to a document whose kind is already
     * known.
     */
    roleName?: string | null;
    subtypeName?: string | null;
}

/** An open question left by one run: which stored object a term of the document means. */
export interface OpenDecision {
    extractedObjectId: string;
    analysisId: string;
    documentName: string;
    term: string;
    className: string | null;
    candidates: any[];
    searchedKey?: string | null;
    searchedValues?: Record<string, any> | null;
}

export interface DocumentImport {
    analyses: AnalysisRow[];
    /** The open questions per run, so a row can say that it has some. */
    decisions: Record<string, OpenDecision[]>;
    decide: (decision: OpenDecision, candidate: any | null) => Promise<void>;
    /**
     * Die Läufe mit einem Plan, der noch angewendet werden kann — nach Dokument-Id.
     *
     * Leer, solange nichts ansteht. Ein Verarbeitungstest steht nie darin: Sein Plan wird gebaut und
     * ist zu lesen, aber er darf den Bestand nicht ändern.
     */
    awaitingMerge: Record<string, AnalysisRow>;
    /** Den Plan eines Laufs anwenden. Nur nach Bestätigung aufzurufen. */
    merge: (analysisId: string) => Promise<MergeReport>;
    loading: boolean;
    /** Live progress of the runs being watched, by job id. */
    progress: Record<string, ImportProgress>;
    /** Dropped but not yet accepted by the server — named so the row does not appear from nowhere. */
    pending: string[];
    /** `folderPath` ist der Ordner, auf den gezogen wurde; ohne ihn der Projektordner. */
    upload: (files: File[], folderPath?: string) => Promise<void>;
    remove: (analysisId: string) => Promise<void>;
    reload: () => Promise<void>;
}

/**
 * The runs of one data room, and the ones currently happening.
 *
 * `dataRoomId` null means there is nothing to show — no project is selected, so no room exists and
 * a listing without one would show every run of the tenant, which is not what the column is about.
 */
export const useDocumentImport = (
    dataRoomId: string | null,
    onError: (error: unknown) => void,
): DocumentImport => {
    const api = useDocumentAnalysisAPI();

    const [analyses, setAnalyses] = useState<AnalysisRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState<Record<string, ImportProgress>>({});
    const [pending, setPending] = useState<string[]>([]);
    /** Wie viele Ströme gerade offen sind — siehe `MAX_WATCHED`. */
    const watched = useRef(0);
    const [decisions, setDecisions] = useState<Record<string, OpenDecision[]>>({});

    /** Streams to close when the room changes or the page goes away. */
    const controllers = useRef<AbortController[]>([]);

    const reload = useCallback(async () => {
        if (!dataRoomId) {
            setAnalyses([]);
            setPending([]);
            setProgress({});
            setDecisions({});
            return;
        }

        setLoading(true);
        try {
            const result = await api.getAnalyses({ dataRoomId, pageSize: 100 });
            const rows = result.analyses ?? [];
            setAnalyses(rows);

            // Asked per run, because a question belongs to the document it came out of — that is
            // what makes it answerable at the row rather than in a list of strangers.
            const open: Record<string, OpenDecision[]> = {};

            await Promise.all(
                rows
                    .filter((row) => row.status === 'completed')
                    .map(async (row) => {
                        try {
                            const staged = await api.getResolution(row._id);

                            const found = (staged.extractedObjects ?? [])
                                .filter((object: any) => {
                                    const status = object?.resolution?.status;
                                    return (
                                        (status === 'ambiguous' || status === 'unresolved') &&
                                        (object?.resolution?.candidates ?? []).length > 0
                                    );
                                })
                                .map((object: any) => ({
                                    extractedObjectId: String(object._id),
                                    analysisId: row._id,
                                    documentName: row.document?.fileName ?? '',
                                    term: object.term,
                                    className: object.className ?? null,
                                    candidates: object.resolution?.candidates ?? [],
                                    searchedKey: object.resolution?.searchedKey ?? null,
                                    searchedValues: object.resolution?.searchedValues ?? null,
                                }));

                            if (found.length > 0) open[row._id] = found;
                        } catch {
                            // One unreadable run must not hide the questions of the others.
                        }
                    }),
            );

            setDecisions(open);
        } catch (error) {
            onError(error);
        } finally {
            setLoading(false);
        }
        // The API object is rebuilt on every render; its behaviour is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataRoomId]);

    useEffect(() => {
        void reload();
    }, [reload]);

    /**
     * Nachlesen, solange irgendein Lauf noch arbeitet.
     *
     * Der Ereignisstrom verfolgt nur eine Handvoll Läufe; alle übrigen — und nach einem Neustart
     * des Servers auch die wieder aufgenommenen — melden sich bei niemandem. Ihr Zustand steht
     * aber im Satz, und den zu lesen genügt. Ohne das bliebe eine Zeile auf „läuft" stehen, bis
     * jemand die Seite neu lädt: derselbe Anblick wie der Fehler, den die Frist behebt, nur diesmal
     * ohne Ursache.
     *
     * Ein Zeitgeber je Runde statt eines Intervalls: Er wird gesetzt, *nachdem* gelesen wurde, und
     * kann sich deshalb nicht überholen, wenn das Lesen einmal länger dauert.
     */
    useEffect(() => {
        if (!analyses.some((analysis: any) => analysis?.status === 'running')) return;

        const timer = setTimeout(() => void reload(), POLL_MS);

        return () => clearTimeout(timer);
    }, [analyses, reload]);

    // A change of room abandons what was being watched: those runs belong to the project that is no
    // longer on screen, and their rows are gone with it.
    useEffect(
        () => () => {
            controllers.current.forEach((controller) => controller.abort());
            controllers.current = [];
        },
        [dataRoomId],
    );

    const follow = useCallback(
        async (jobId: string) => {
            const controller = new AbortController();
            controllers.current.push(controller);

            const apply = (event: AnalysisEvent) => {
                setProgress((current) => {
                    const previous = current[jobId] ?? {};

                    // The type and the subtype, kept the moment the step that decides them completes
                    // — everything after only adds to what the document is taken to be.
                    const named =
                        event.step === 'roleClassification' && event.status === 'completed'
                            ? { roleName: event.payload?.role ?? null }
                            : event.step === 'subtypeClassification' && event.status === 'completed'
                              ? { subtypeName: readableSubtype(event.payload) }
                              : {};

                    if (event.step === 'job') {
                        return {
                            ...current,
                            [jobId]: {
                                ...previous,
                                finished: true,
                                failed: event.status === 'error',
                                message: event.message,
                            },
                        };
                    }

                    return {
                        ...current,
                        [jobId]: {
                            ...previous,
                            ...named,
                            step: event.step,
                            message: event.message,
                            failed: event.status === 'failed',
                        },
                    };
                });
            };

            try {
                await api.streamAnalysisJob(jobId, 0, apply, controller.signal);
            } catch (error) {
                if (!controller.signal.aborted) {
                    // A lost stream is not a lost run: the server carries on, and the reload below
                    // will show what became of it. Remove progress rather than marking finished,
                    // so the row falls back to row.status.
                    setProgress((current) => {
                        const { [jobId]: _removed, ...rest } = current;
                        return rest;
                    });
                }
            } finally {
                controllers.current = controllers.current.filter((entry) => entry !== controller);
            }

            // The row's numbers come from the record, so it has to be read again once the run ends.
            // Skip reload if aborted: the stream was stopped because the room changed, and this
            // reload would query the old dataRoomId and overwrite the current list.
            if (!controller.signal.aborted) {
                await reload();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reload],
    );

    /**
     * Send the files, one run each, a few at a time.
     *
     * Sequential within each slot rather than all at once: the upload of the fourth file waits for
     * a stream to free up, which is also what keeps the page responsive while ten documents are
     * being read.
     */
    const upload = useCallback(
        async (files: File[], folderPath?: string) => {
            if (!dataRoomId || files.length === 0) return;

            setPending((current) => [...current, ...files.map((file) => file.name)]);

            const queue = [...files];

            const worker = async () => {
                for (;;) {
                    const file = queue.shift();
                    if (!file) return;

                    const done = () =>
                        setPending((current) => {
                            const index = current.indexOf(file.name);
                            return index < 0
                                ? current
                                : [...current.slice(0, index), ...current.slice(index + 1)];
                        });

                    try {
                        // `dataRoom` als Herkunft: Das hier *ist* der Dateimanager des Datenraums,
                        // und nur ein von hier gestarteter Lauf darf später den Bestand ändern.
                        const { jobId } = await api.startAnalysisJob(
                            file,
                            dataRoomId,
                            folderPath,
                            'dataRoom',
                        );

                        // Marked as running before the first event arrives, so the row that the
                        // reload brings in shows a step rather than a finished-looking blank.
                        setProgress((current) => ({
                            ...current,
                            [jobId]: { step: 'conversion' },
                        }));

                        // The record exists by the time the upload answers, so the row appears here
                        // — while the run is still converting. Its own placeholder goes at the same
                        // moment: two rows for one document is worse than none.
                        await reload();
                        done();

                        /**
                         * Verfolgt wird, wofür eine Verbindung frei ist — und nicht gewartet.
                         *
                         * Auf das Ende des Laufs zu warten, bevor die nächste Datei hochgeht, wäre
                         * die alte Drosselung im Browser: Der Server nähme dann nie mehr als drei
                         * Aufträge entgegen, und seine Schlange bliebe leer. Wer nicht verfolgt
                         * wird, wird nachgelesen — siehe unten.
                         */
                        if (watched.current < MAX_WATCHED) {
                            watched.current += 1;
                            void follow(jobId).finally(() => {
                                watched.current -= 1;
                            });
                        }
                    } catch (error) {
                        done();
                        onError(error);
                    }
                }
            };

            await Promise.all(
                Array.from({ length: Math.min(MAX_UPLOADS, files.length) }, () => worker()),
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dataRoomId, reload, follow],
    );

    const remove = useCallback(
        async (analysisId: string) => {
            try {
                await api.deleteAnalysis(analysisId);
                await reload();
            } catch (error) {
                onError(error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reload],
    );

    const decide = useCallback(
        async (decision: OpenDecision, candidate: any | null) => {
            try {
                if (candidate) {
                    await api.bindExtractedObject(
                        decision.extractedObjectId,
                        candidate.objectId,
                        candidate.className,
                    );
                } else {
                    await api.rejectExtractedObject(decision.extractedObjectId);
                }

                await reload();
            } catch (error) {
                onError(error);
                throw error;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reload],
    );

    /**
     * Den Plan eines Laufs anwenden.
     *
     * Der Aufrufer hat den Dialog gezeigt und die Zustimmung eingeholt; hier wird sie nur noch
     * weitergereicht. Danach neu geladen, weil sich mit dem Anwenden der Zustand der Zeile ändert —
     * aus „steht bereit" wird „angewendet", und der Knopf soll verschwinden.
     */
    const merge = useCallback(
        async (analysisId: string) => {
            try {
                const { report } = await api.mergeAnalysis(analysisId);

                await reload();

                return report;
            } catch (error) {
                onError(error);
                throw error;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [reload],
    );

    /**
     * Die Läufe, für die ein Plan bereitsteht — nach der Datei, wie die Ansicht sie kennt.
     *
     * Nach `managedFileId` und nicht nach `documentId`: Der Dateibaum zeigt Dateien der Ablage und
     * schlägt seine Läufe unter deren Id nach (`analysesByFileIds` tut dasselbe). `documentId`
     * bezeichnet dagegen den *Inhalt* — derselbe Vertrag bleibt über mehrere Uploads hinweg
     * dasselbe Dokument. Zwei Id-Räume, die sich nie treffen; danach zu schlüsseln hiess, dass die
     * Ansicht nie einen Treffer fand und die Schaltfläche nie erschien.
     */
    const awaitingMerge = useMemo(() => {
        const byFile: Record<string, AnalysisRow> = {};

        analyses.forEach((row) => {
            if (row.mergeStatus !== 'awaiting') return;

            const fileId = row.document?.managedFileId;

            if (fileId) byFile[String(fileId)] = row;
        });

        return byFile;
    }, [analyses]);

    return {
        analyses,
        loading,
        progress,
        pending,
        decisions,
        decide,
        awaitingMerge,
        merge,
        upload,
        remove,
        reload,
    };
};
